import React, { useState, useMemo } from 'react';
import { Search, Plus, GitBranch, Play, Clock, Zap, Timer, ChevronRight, Activity } from 'lucide-react';
import { getUserWorkflows, getCategories } from '../../data/workflowsData';
import { AGENT_THUMBS } from './V4BriefingShared';

const AGENT_LABELS = { marsi: 'Marsi', sally: 'Sally', luc: 'Luc', stark: 'Stark', finn: 'Finn', dora: 'Dora', buzz: 'Buzz', zira: 'Zira', tali: 'Tali' };

const Toggle = ({ active, onToggle, isDark }) => (
  <button
    onClick={(e) => { e.stopPropagation(); onToggle(); }}
    className={`relative w-8 h-[18px] rounded-full transition-colors flex-shrink-0 ${active ? 'bg-emerald-500/80' : (isDark ? 'bg-white/10' : 'bg-black/10')}`}
    data-testid="workflow-toggle"
  >
    <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white shadow transition-transform ${active ? 'left-[16px]' : 'left-[2px]'}`} />
  </button>
);

const CategoryPill = ({ category, isDark }) => {
  const colors = {
    personal: isDark ? 'bg-indigo-500/10 text-indigo-400' : 'bg-indigo-50 text-indigo-600',
    productivity: isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-600',
    research: isDark ? 'bg-cyan-500/10 text-cyan-400' : 'bg-cyan-50 text-cyan-600',
    strategy: isDark ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-600',
    marketing: isDark ? 'bg-rose-500/10 text-rose-400' : 'bg-rose-50 text-rose-600',
    sales: isDark ? 'bg-amber-500/10 text-amber-400' : 'bg-amber-50 text-amber-600',
    support: isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-600',
    finance: isDark ? 'bg-green-500/10 text-green-400' : 'bg-green-50 text-green-600',
    people: isDark ? 'bg-fuchsia-500/10 text-fuchsia-400' : 'bg-fuchsia-50 text-fuchsia-600',
  };
  return <span className={`text-[9px] font-medium px-2 py-0.5 rounded-full capitalize ${colors[category] || ''}`}>{category}</span>;
};

const TriggerBadge = ({ type, isDark }) => {
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const icons = { manual: Zap, scheduled: Clock, event: Activity };
  const Icon = icons[type] || Zap;
  return (
    <span className={`flex items-center gap-1 text-[9px] ${ts}`}>
      <Icon className="w-2.5 h-2.5" />{type === 'event' ? 'Event' : type === 'scheduled' ? 'Scheduled' : 'Manual'}
    </span>
  );
};

const WorkflowCard = ({ wf, isDark, onView, onToggle }) => {
  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const cardBg = isDark ? 'bg-[#0d1b2a]' : 'bg-[#f0ece4]';
  const hoverBg = isDark ? 'hover:bg-[#0f2035]' : 'hover:bg-[#ece5db]';

  return (
    <div
      className={`${cardBg} border ${border} rounded-xl p-4 ${hoverBg} transition-colors cursor-pointer group`}
      onClick={() => onView(wf.id)}
      data-testid={`workflow-card-${wf.id}`}
    >
      <div className="flex items-start justify-between mb-2.5">
        <div className="flex items-center gap-2 min-w-0">
          {AGENT_THUMBS[wf.agent] && <img src={AGENT_THUMBS[wf.agent]} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />}
          <div className="min-w-0">
            <p className={`text-[12px] font-semibold ${tp} truncate`}>{wf.name}</p>
            <span className={`text-[9px] ${ts}`}>{AGENT_LABELS[wf.agent] || wf.agent}</span>
          </div>
        </div>
        <Toggle active={wf.active} onToggle={() => onToggle(wf.id)} isDark={isDark} />
      </div>

      <p className={`text-[10px] leading-relaxed ${ts} mb-3 line-clamp-2`}>{wf.description}</p>

      <div className="flex items-center gap-2 mb-3">
        <CategoryPill category={wf.category} isDark={isDark} />
        <TriggerBadge type={wf.trigger.type} isDark={isDark} />
      </div>

      <div className={`flex items-center justify-between pt-2.5 border-t ${border}`}>
        <div className="flex items-center gap-3">
          <span className={`text-[9px] ${ts}`}><span className={`font-semibold ${tp}`}>{wf.runCount}</span> runs</span>
          <span className={`text-[9px] ${ts}`}>{wf.successRate} success</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); }}
            className="flex items-center gap-1 px-2 py-1 rounded-md bg-gradient-to-r from-indigo-500 to-purple-600 text-white text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`workflow-run-${wf.id}`}
          >
            <Play className="w-2.5 h-2.5" /> Run
          </button>
          <ChevronRight className={`w-3.5 h-3.5 ${ts} opacity-0 group-hover:opacity-100 transition-opacity`} />
        </div>
      </div>
    </div>
  );
};

const V4WorkflowsList = ({ isDark, onViewWorkflow, onCreateWorkflow, activeUser }) => {
  const userDept = activeUser?.id || 'ceo';
  const categories = useMemo(() => getCategories(userDept), [userDept]);
  const baseWorkflows = useMemo(() => getUserWorkflows(userDept), [userDept]);

  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [typeTab, setTypeTab] = useState('all');
  const [localWorkflows, setLocalWorkflows] = useState(baseWorkflows);

  // Reset when user switches
  React.useEffect(() => {
    setLocalWorkflows(baseWorkflows);
    setCategoryFilter('all');
    setTypeTab('all');
    setSearch('');
  }, [baseWorkflows]);

  const tp = isDark ? 'text-white' : 'text-slate-800';
  const ts = isDark ? 'text-white/40' : 'text-slate-500';
  const border = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';
  const inputBg = isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]';
  const hoverBg = isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-black/[0.04]';

  const toggleActive = (id) => {
    setLocalWorkflows(prev => prev.map(wf => wf.id === id ? { ...wf, active: !wf.active } : wf));
  };

  const filtered = useMemo(() => {
    return localWorkflows.filter(wf => {
      if (categoryFilter !== 'all' && wf.category !== categoryFilter) return false;
      if (typeTab === 'pre-built' && wf.type !== 'pre-built') return false;
      if (typeTab === 'custom' && wf.type !== 'custom') return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        return wf.name.toLowerCase().includes(q) || wf.description.toLowerCase().includes(q) || wf.agent.toLowerCase().includes(q);
      }
      return true;
    });
  }, [localWorkflows, categoryFilter, typeTab, search]);

  const preBuiltCount = localWorkflows.filter(w => w.type === 'pre-built').length;
  const customCount = localWorkflows.filter(w => w.type === 'custom').length;

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="workflows-list-page">
      {/* Header */}
      <div className="px-5 pt-4 pb-3 flex-shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <GitBranch className={`w-5 h-5 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
            <p className={`text-base font-semibold ${tp}`}>Workflows</p>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${isDark ? 'bg-white/[0.06] text-white/40' : 'bg-black/[0.04] text-slate-500'}`}>
              {localWorkflows.length} total
            </span>
          </div>
          <button
            onClick={onCreateWorkflow}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium bg-gradient-to-br from-indigo-500 to-purple-600 text-white hover:opacity-90 transition-opacity"
            data-testid="workflows-create-btn"
          >
            <Plus className="w-3.5 h-3.5" /> Create Workflow
          </button>
        </div>

        {/* Search */}
        <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${inputBg} border ${border} mb-3`}>
          <Search className={`w-3.5 h-3.5 ${ts}`} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search workflows by name, agent, or description..."
            className={`flex-1 bg-transparent outline-none text-xs ${tp} placeholder:${ts}`}
            data-testid="workflows-search"
          />
        </div>

        {/* Type tabs */}
        <div className="flex items-center gap-1 mb-3">
          {[
            { id: 'all', label: `All (${localWorkflows.length})` },
            { id: 'pre-built', label: `Pre-built (${preBuiltCount})` },
            { id: 'custom', label: `Custom (${customCount})` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setTypeTab(tab.id)}
              className={`px-3 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${typeTab === tab.id
                ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white'
                : `${ts} ${hoverBg}`
              }`}
              data-testid={`workflows-tab-${tab.id}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Category filter pills */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setCategoryFilter(cat.id)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors ${categoryFilter === cat.id
                ? (isDark ? 'bg-white/[0.12] text-white' : 'bg-black/[0.08] text-slate-800')
                : `${ts} ${hoverBg}`
              }`}
              data-testid={`workflows-cat-${cat.id}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`border-t ${border}`} />

      {/* Workflow grid */}
      <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <GitBranch className={`w-8 h-8 mb-3 ${ts}`} />
            <p className={`text-sm font-medium ${tp} mb-1`}>No workflows found</p>
            <p className={`text-xs ${ts}`}>Try adjusting your filters or search query</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-3">
            {filtered.map(wf => (
              <WorkflowCard
                key={wf.id}
                wf={wf}
                isDark={isDark}
                onView={onViewWorkflow}
                onToggle={toggleActive}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default V4WorkflowsList;
